import type { WithRequiredTheme } from '@opallabs/lapidary'
import { linkStyles } from './styles'
import { ReferencedObject } from './types'

type RenderObjectLinkProps = Readonly<
  WithRequiredTheme & {
    referencedObject: ReferencedObject
    idx: number
    asAnchor?: boolean
  }
>
// refs inside a kept [[...]] segment render as their own anchor, which
// is independent of the notification itself being its own anchor
const renderObjectLink = ({
  idx,
  referencedObject,
  theme,
  asAnchor = false,
}: RenderObjectLinkProps): React.ReactNode => {
  if (referencedObject === undefined) return null
  const useLink = referencedObject.model_type !== 'user'

  if (asAnchor && useLink && referencedObject.url) {
    return (
      <a
        href={referencedObject.url}
        target="_blank"
        rel="noopener noreferrer"
        css={linkStyles({ theme })}
        key={idx}
        onClick={(e) => e.stopPropagation()}
      >
        {referencedObject.text}
      </a>
    )
  }

  return (
    <span css={useLink && linkStyles({ theme })} key={idx}>
      {referencedObject.text}
    </span>
  )
}

const getReferredObject = (
  objectType: string,
  referencedObjects: ReferencedObject[]
): ReferencedObject =>
  referencedObjects.filter(
    (referredObject) => referredObject.model_type === objectType
  )[0]

type PopulateNotificationTemplateProps = Readonly<
  WithRequiredTheme & {
    notificationTemplate: string
    referencedObjects: ReferencedObject[]
    supplementalText: string
  }
>
// does a %{placeholder} have a rendered value available?
const canResolvePlaceholder = (
  name: string,
  referencedObjects: ReferencedObject[],
  supplementalText: string
): boolean => {
  if (name === 'supplemental_text') return Boolean(supplementalText)
  return Boolean(getReferredObject(name, referencedObjects))
}

// first strips any unresolved [[...]] optional segments, then substitutes
// each %{placeholder} with either a linked referenced object or supplemental text.
export const populateNotificationTemplate = ({
  notificationTemplate,
  referencedObjects,
  supplementalText,
  theme,
}: PopulateNotificationTemplateProps): React.ReactNode[] => {
  // placeholders seen inside [[...]] segments render as their own
  // anchor instead of styled spans
  const anchorPlaceholders = new Set<string>()

  // any `[[...]]` segment is kept only if every inner `%{placeholder}` can be
  // resolved. otherwise the whole segment is dropped.
  const stripped = notificationTemplate.replace(
    /\[\[([^[\]]*?)\]\]/g,
    (_match, inner: string) => {
      const placeholders = inner.match(/%{(\w+)}/g) ?? []
      const allResolve = placeholders.every((ph) =>
        canResolvePlaceholder(
          ph.slice(2, -1),
          referencedObjects,
          supplementalText
        )
      )
      if (!allResolve) return ''

      placeholders.forEach((ph) => anchorPlaceholders.add(ph.slice(2, -1)))
      return inner
    }
  )

  const splitTemplate = stripped.split(/(%{\w+})/)

  const renderNodes = splitTemplate.map((textPartial, idx) => {
    const matchedObject = textPartial.match(/%{(\w+)}/)

    if (matchedObject) {
      let referredObject
      if (matchedObject[1] === 'space') {
        referredObject =
          getReferredObject('assetreference', referencedObjects) ||
          getReferredObject('post', referencedObjects) ||
          getReferredObject('moment', referencedObjects) ||
          getReferredObject('story', referencedObjects) ||
          getReferredObject('blocknotificationdata', referencedObjects) ||
          getReferredObject('board', referencedObjects)
      }
      if (!referredObject) {
        referredObject = getReferredObject(matchedObject[1], referencedObjects)
      }

      if (referredObject && referredObject.url) {
        return renderObjectLink({
          idx,
          referencedObject: referredObject,
          theme,
          asAnchor: anchorPlaceholders.has(matchedObject[1]),
        })
      }
      if (matchedObject[1] === 'supplemental_text') {
        return supplementalText
      }
    }

    return <span key={idx}>{textPartial}</span>
  })
  return renderNodes
}
